import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, PlayCircle, Sparkles } from "lucide-react";

/**
 * Public auto-tour. Mounted globally at App root.
 *
 * Lifecycle:
 *   1. Visitor clicks "Watch Demo" -> startDemoTour() helper (below)
 *      - Calls POST /api/auth/demo-login -> short-lived JWT for "demo" user
 *      - Stores tribe_token + sessionStorage flags
 *      - Hard navigates to /feed (reloads -> AuthContext picks up token)
 *   2. This component reads sessionStorage flag and runs auto-navigation +
 *      shows banner/caption + click-blocker overlay for ~130 seconds.
 *   3. On completion or Exit, clears flag, removes token, navigates to /register
 *      (or /welcome for explicit exit).
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const trackDemoEvent = (sessionId, event, extra = {}) => {
  try {
    axios.post(`${API}/demo/track`, { session_id: sessionId, event, ...extra }).catch(() => {});
  } catch { /* silent */ }
};

const STOPS = [
  { path: "/feed", duration: 13000, title: "Social Feed", caption: "Real-time AI community feed — posts, polls, link previews, reactions, comments & analytics." },
  { path: "/create-post", duration: 12000, title: "Create Post", caption: "Compose rich posts with images, videos, polls, hashtags & auto link unfurling — built for AI builders." },
  { path: "/workspace", duration: 14000, title: "AI Tools", caption: "12+ AI tools in one workspace — Resume Builder, Code Assistant, Image & Video Generator, Voice Notes, PPT, Email & more." },
  { path: "/courses", duration: 13000, title: "AI Courses", caption: "Structured AI courses with lessons, quizzes, certificates & AI-powered study groups." },
  { path: "/tutorials", duration: 13000, title: "AI Tutorials", caption: "Hands-on AI tutorials — learn step-by-step with code, examples & community discussions." },
  { path: "/webinars", duration: 14000, title: "Events, Webinars & Hackathons", caption: "Host or join live webinars with HD streaming, paid tickets, GST invoicing, hackathons & RSVP events." },
  { path: "/news", duration: 12000, title: "AI News", caption: "Curated AI news hub — track trends, breakthroughs & industry insights from global sources." },
  { path: "/jobs", duration: 13000, title: "Jobs Board", caption: "AI-matched jobs with 1-click apply, recruiter dashboard, resume shortlisting & talent pipeline." },
  { path: "/investor-hub", duration: 13000, title: "Investor Hub", caption: "Connect startups with investors — AI matching, private deal rooms, pitch decks & NDAs." },
  { path: "/ai-hub", duration: 13000, title: "AI Hub & Marketplace", caption: "Discover AI models, agents, templates & sell your AI work in the marketplace." },
];

const TOTAL_DURATION = STOPS.reduce((s, x) => s + x.duration, 0);

/**
 * Public helper — call this from any "Watch Demo" button.
 * Performs demo login, sets sessionStorage flag, then hard-reloads into the tour.
 */
export const startDemoTour = async () => {
  try {
    const res = await axios.post(`${API}/auth/demo-login`);
    const { access_token } = res.data;

    // Preserve any existing user session so we can restore it if they exit
    const prev = localStorage.getItem("tribe_token");
    if (prev && prev !== access_token) {
      sessionStorage.setItem("tribe_token_prev", prev);
    }
    localStorage.setItem("tribe_token", access_token);

    // Flags read by the global <DemoAutoTour /> mounted in App
    const sid = `demo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem("aixtribe_demo_active", "1");
    sessionStorage.setItem("aixtribe_demo_session_id", sid);
    sessionStorage.setItem("aixtribe_demo_started_at", Date.now().toString());
    sessionStorage.setItem("aixtribe_demo_stop", "0");

    // Analytics: tour started
    trackDemoEvent(sid, "started");
    trackDemoEvent(sid, "stop_viewed", { stop_idx: 0, stop_title: STOPS[0].title });

    // Hard navigate so AuthContext re-initialises with the demo token
    window.location.href = STOPS[0].path;
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not start demo. Please try again.");
  }
};

const cleanupDemoSession = () => {
  sessionStorage.removeItem("aixtribe_demo_active");
  sessionStorage.removeItem("aixtribe_demo_started_at");
  sessionStorage.removeItem("aixtribe_demo_stop");
  const prev = sessionStorage.getItem("tribe_token_prev");
  if (prev) {
    localStorage.setItem("tribe_token", prev);
    sessionStorage.removeItem("tribe_token_prev");
  } else {
    localStorage.removeItem("tribe_token");
  }
};

const DemoAutoTour = () => {
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const stopTimerRef = useRef(null);
  const tickRef = useRef(null);

  // Initial mount: check if demo is active in this session
  useEffect(() => {
    if (sessionStorage.getItem("aixtribe_demo_active") === "1") {
      const startedAt = parseInt(sessionStorage.getItem("aixtribe_demo_started_at") || "0", 10);
      const savedIdx = parseInt(sessionStorage.getItem("aixtribe_demo_stop") || "0", 10);
      const ageMs = Date.now() - startedAt;
      if (!startedAt || ageMs >= TOTAL_DURATION) {
        cleanupDemoSession();
        return;
      }
      setIdx(savedIdx);
      setElapsed(ageMs);
      setActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tick elapsed counter
  useEffect(() => {
    if (!active) return;
    const startedAt = parseInt(sessionStorage.getItem("aixtribe_demo_started_at") || "0", 10);
    tickRef.current = setInterval(() => {
      const e = Date.now() - startedAt;
      setElapsed(e);
      if (e >= TOTAL_DURATION) {
        clearInterval(tickRef.current);
        finishTour();
      }
    }, 250);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Schedule next stop based on the total elapsed time (resilient to reloads)
  useEffect(() => {
    if (!active) return;
    clearTimeout(stopTimerRef.current);

    const startedAt = parseInt(sessionStorage.getItem("aixtribe_demo_started_at") || "0", 10);

    // Recompute which stop we *should* be on based on elapsed time
    let cum = 0;
    let expectedIdx = STOPS.length - 1;
    for (let i = 0; i < STOPS.length; i++) {
      cum += STOPS[i].duration;
      if (Date.now() - startedAt < cum) { expectedIdx = i; break; }
    }
    if (expectedIdx !== idx) {
      setIdx(expectedIdx);
      sessionStorage.setItem("aixtribe_demo_stop", String(expectedIdx));
      if (location.pathname !== STOPS[expectedIdx].path) {
        navigate(STOPS[expectedIdx].path);
      }
      return;
    }

    // Time until next hop
    const elapsedTotal = Date.now() - startedAt;
    let untilNext = 0;
    let acc = 0;
    for (let i = 0; i <= idx; i++) acc += STOPS[i].duration;
    untilNext = Math.max(50, acc - elapsedTotal);

    stopTimerRef.current = setTimeout(() => {
      if (idx < STOPS.length - 1) {
        const next = idx + 1;
        // Log time spent on current stop
        const sid = sessionStorage.getItem("aixtribe_demo_session_id") || "unknown";
        trackDemoEvent(sid, "stop_viewed", {
          stop_idx: next,
          stop_title: STOPS[next].title,
          time_on_stop_ms: STOPS[idx].duration,
        });
        setIdx(next);
        sessionStorage.setItem("aixtribe_demo_stop", String(next));
        navigate(STOPS[next].path);
      } else {
        finishTour();
      }
    }, untilNext);

    return () => clearTimeout(stopTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, active, location.pathname]);

  const finishTour = useCallback(() => {
    clearTimeout(stopTimerRef.current);
    clearInterval(tickRef.current);
    const sid = sessionStorage.getItem("aixtribe_demo_session_id") || "unknown";
    trackDemoEvent(sid, "completed");
    cleanupDemoSession();
    setActive(false);
    // Hard navigate so AuthContext re-reads stripped token
    window.location.href = `/register?from=demo&sid=${encodeURIComponent(sid)}`;
  }, []);

  const exitTour = useCallback(() => {
    clearTimeout(stopTimerRef.current);
    clearInterval(tickRef.current);
    const sid = sessionStorage.getItem("aixtribe_demo_session_id") || "unknown";
    const lastIdx = parseInt(sessionStorage.getItem("aixtribe_demo_stop") || "0", 10);
    trackDemoEvent(sid, "exited", { stop_idx: lastIdx, stop_title: STOPS[lastIdx]?.title });
    cleanupDemoSession();
    setActive(false);
    window.location.href = "/welcome";
  }, []);

  if (!active) return null;

  const remainingMs = Math.max(0, TOTAL_DURATION - elapsed);
  const remainingSec = Math.ceil(remainingMs / 1000);
  const mm = Math.floor(remainingSec / 60).toString().padStart(2, "0");
  const ss = (remainingSec % 60).toString().padStart(2, "0");
  const progressPct = Math.min(100, (elapsed / TOTAL_DURATION) * 100);
  const stop = STOPS[idx];

  return createPortal(
    <>
      {/* Click-blocker overlay — covers EVERYTHING, captures all pointer events */}
      <div
        className="fixed inset-0 z-[150] bg-transparent cursor-not-allowed"
        style={{ pointerEvents: "auto" }}
        onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onMouseDownCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onTouchStartCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onContextMenu={(e) => e.preventDefault()}
        data-testid="demo-click-blocker"
        aria-hidden="true"
      />

      {/* Top banner */}
      <div
        className="fixed top-0 left-0 right-0 z-[200] bg-gradient-to-r from-[#0A0A0F]/95 via-[#15151F]/95 to-[#0A0A0F]/95 backdrop-blur-xl border-b border-cyan-500/30 shadow-2xl"
        style={{ pointerEvents: "auto" }}
        data-testid="demo-top-banner"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 flex items-center gap-3">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="relative w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold">Demo Mode</span>
              <span className="text-[10px] text-gray-500">Read-only auto-tour</span>
            </div>
          </div>

          {/* Progress */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-white font-semibold truncate" data-testid="demo-current-title">
                <span className="text-tribe-cyan">{idx + 1}/{STOPS.length}</span> · {stop.title}
              </span>
              <span className="text-xs text-gray-400 font-mono flex-shrink-0 ml-2" data-testid="demo-time-remaining">
                {mm}:{ss}
              </span>
            </div>
            <div className="h-1 bg-[#1A1A24] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 transition-all duration-200"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          <button
            onClick={exitTour}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-gray-300 hover:text-red-300 text-xs font-semibold transition-all"
            style={{ pointerEvents: "auto" }}
            data-testid="demo-exit-btn"
          >
            <X className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Exit Demo</span>
          </button>
        </div>
      </div>

      {/* Bottom caption */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[200] bg-gradient-to-t from-[#0A0A0F]/98 via-[#0A0A0F]/95 to-transparent pt-10 pb-4 px-3 sm:px-6"
        style={{ pointerEvents: "none" }}
        data-testid="demo-caption"
      >
        <div className="max-w-3xl mx-auto bg-[#15151F]/95 border border-cyan-500/20 rounded-2xl px-5 py-3.5 shadow-2xl backdrop-blur-xl">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <PlayCircle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold mb-0.5">Now showing</p>
              <p className="text-white text-sm sm:text-base font-semibold">{stop.title}</p>
              <p className="text-gray-400 text-xs sm:text-sm mt-1 leading-snug">{stop.caption}</p>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default DemoAutoTour;
